import React, { useState, useEffect, useMemo } from "react";
import {
    FormControl,
    InputLabel,
    MenuItem,
    ListSubheader,
    Box,
    CircularProgress,
    Button,
    Divider,
    Typography,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
    Tooltip,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { Business as BusinessIcon } from "components/MaterialSymbol/MaterialIcons";
import { ExpandMore as ExpandMoreIcon } from "components/MaterialSymbol/MaterialIcons";
import useDealersSwr from "hooks/useDealersSwr";

const useStyles = makeStyles((theme) => ({
    field: {
        width: 300,
        "& .MuiFilledInput-root": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
            },
        },
        cursor: "pointer",
    },
    dot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        marginRight: 8,
        display: "inline-block",
    },
    activeDot: {
        backgroundColor: theme.palette.success.light,
    },
    inactiveDot: {
        backgroundColor: theme.palette.text.disabled,
    },
    popoverContent: {
        width: 300,
        maxHeight: 450,
        overflowY: "auto",
    },
    groupHeader: {
        backgroundColor: theme.palette.background.paper,
        lineHeight: "28px",
        fontSize: "0.75rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: theme.palette.text.secondary,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    listItem: {
        paddingTop: 1,
        paddingBottom: 1,
        "& .MuiListItemText-primary": {
            fontSize: "0.8125rem",
        },
    },
    listDot: { // Separate class for items to avoid baseline shifts
        width: 6,
        height: 6,
        borderRadius: "50%",
        marginRight: 12,
        flexShrink: 0,
    },
    inactiveText: {
        color: theme.palette.text.disabled,
    },
    loadMoreContainer: {
        padding: theme.spacing(0.5, 1),
        textAlign: "center",
    },
    skeletonItem: {
        padding: theme.spacing(0.5, 2),
        display: "flex",
        alignItems: "center",
        height: 24,
    },
    selectAllAction: {
        width: "auto",
        margin: theme.spacing(1, 1, 0.5, 1),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
        },
        "& .MuiListItemText-primary": {
            fontWeight: 700,
            fontSize: "0.8125rem",
            color: theme.palette.primary.main,
        },
        "& .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
        },
    },
}));

const FOR_A_SPECIFIC_DEALER = "1";
const INACTIVE_LIMIT = 10;

export default function SelectDealerGrouped({ showAllDealers = false }) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [dlid, setDlid] = useState(0);
    const scrollRef = React.useRef(null);
    const [isManualLoading, setIsManualLoading] = useState(false);
    const _sdlid = localStorage.getItem("_sdlid");

    const { dealers: activeDealers = [], isMultiDomain, isLoading: isLoadingActive } = useDealersSwr(undefined, undefined, undefined, "", true, "1");

    const [inactivePage, setInactivePage] = useState(1);
    const [accumulatedInactive, setAccumulatedInactive] = useState([]);
    const { dealers: inactiveData = {}, isLoading: isLoadingInactive } = useDealersSwr(undefined, inactivePage, INACTIVE_LIMIT, "", true, "0");

    useEffect(() => {
        if (inactiveData?.data) {
            setAccumulatedInactive((prev) => {
                if (inactivePage === 1) return inactiveData.data;
                const existingIds = new Set(prev.map((d) => d.id));
                const newItems = inactiveData.data.filter((d) => !existingIds.has(d.id));
                return [...prev, ...newItems];
            });
            setIsManualLoading(false);
        }
    }, [inactiveData, inactivePage]);

    // Auto-scroll when manual loading starts
    useEffect(() => {
        if (isManualLoading && scrollRef.current) {
            setTimeout(() => {
                if (scrollRef.current) {
                    scrollRef.current.scrollTo({
                        top: scrollRef.current.scrollHeight,
                        behavior: "smooth"
                    });
                }
            }, 50);
        }
    }, [isManualLoading]);

    useEffect(() => {
        const storedDlid = localStorage.getItem("_dlid");
        if (storedDlid) {
            setDlid(parseInt(storedDlid));
        }
    }, []);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => {
        setAnchorEl(null);
        setIsManualLoading(false);
    };

    const handleSelect = (id) => {
        setDlid(id);
        localStorage.setItem("_dlid", id);
        handleClose();
        location.reload();
    };

    const handleLoadMore = (e) => {
        e.stopPropagation();
        setIsManualLoading(true);
        setInactivePage((prev) => prev + 1);
    };

    const selectedDealer = useMemo(() => {
        if (dlid === 0) return { company: "All Dealers", isActive: 1 };
        // Use loose equality or cast to handled potential type mismatches from localStorage/API
        const found = [...activeDealers, ...accumulatedInactive].find(d => Number(d.id) === Number(dlid));
        if (found) return found;
        return { company: `Dealer #${dlid}`, isActive: 1 };
    }, [dlid, activeDealers, accumulatedInactive]);

    const hasMoreInactive =
        accumulatedInactive.length > 0 &&
        accumulatedInactive.length < (inactiveData?.total || 0) &&
        (inactiveData?.data?.length === INACTIVE_LIMIT);
    const open = Boolean(anchorEl);
    const showSkeleton = isManualLoading || (isLoadingInactive && accumulatedInactive.length > 0);

    if (_sdlid === FOR_A_SPECIFIC_DEALER && !isMultiDomain) return null;

    return (
        <Box>
            <TextField
                select
                label="Dealers"
                variant="filled"
                value={dlid}
                onClick={handleOpen}
                className={classes.field}
                SelectProps={{
                    renderValue: () => (
                        <Box display="flex" alignItems="center">
                            <Box
                                className={`${classes.dot} ${selectedDealer?.isActive === 1 ? classes.activeDot : classes.inactiveDot
                                    }`}
                            />
                            {selectedDealer?.company || ""}
                        </Box>
                    ),
                    open: false,
                    onOpen: (e) => {
                        e.preventDefault();
                        handleOpen(e);
                    }
                }}
                InputProps={{
                    readOnly: true,
                    disableUnderline: false,
                    style: { cursor: "pointer" }
                }}
            >
                <MenuItem value={dlid}>
                    {selectedDealer?.company || ""}
                </MenuItem>
            </TextField>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Box className={classes.popoverContent} ref={scrollRef}>
                    <List subheader={<li />}>
                        {isLoadingActive && activeDealers.length === 0 ? (
                            <Box p={3} textAlign="center">
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                {showAllDealers && (
                                    <ListItem
                                        button
                                        className={classes.selectAllAction}
                                        onClick={() => handleSelect(0)}
                                        selected={dlid === 0}
                                    >
                                        <ListItemIcon style={{ minWidth: 32 }}>
                                            <BusinessIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary="Select All dealers" />
                                    </ListItem>
                                )}

                                <ListSubheader className={classes.groupHeader}>Active dealers</ListSubheader>
                                {activeDealers.map((dealer) => (
                                    <ListItem
                                        key={dealer.id}
                                        button
                                        className={classes.listItem}
                                        onClick={() => handleSelect(dealer.id)}
                                        selected={dlid === dealer.id}
                                    >
                                        <Box className={`${classes.listDot} ${classes.activeDot}`} />
                                        <ListItemText primary={dealer.company} title={dealer.company} />
                                    </ListItem>
                                ))}

                                <ListSubheader className={classes.groupHeader}>Inactive dealers</ListSubheader>
                                {accumulatedInactive.map((dealer) => (
                                    <ListItem
                                        key={dealer.id}
                                        button
                                        className={classes.listItem}
                                        onClick={() => handleSelect(dealer.id)}
                                        selected={dlid === dealer.id}
                                    >
                                        <Box className={`${classes.listDot} ${classes.inactiveDot}`} />
                                        <ListItemText
                                            primary={dealer.company}
                                            className={classes.inactiveText}
                                            title={dealer.company}
                                        />
                                    </ListItem>
                                ))}

                                {showSkeleton && (
                                    <>
                                        {[...Array(INACTIVE_LIMIT)].map((_, i) => (
                                            <Box key={`skeleton-${i}`} className={classes.skeletonItem}>
                                                <Skeleton variant="circle" width={5} height={5} style={{ marginRight: 14, backgroundColor: "rgba(0, 0, 0, 0.12)" }} />
                                                <Skeleton variant="text" width="85%" height={16} style={{ backgroundColor: "rgba(0, 0, 0, 0.12)" }} />
                                            </Box>
                                        ))}
                                    </>
                                )}

                                {hasMoreInactive && !showSkeleton && (
                                    <Box className={classes.loadMoreContainer}>
                                        <Tooltip
                                            title={`Fetch next ${INACTIVE_LIMIT} inactive stores`}
                                            placement="top"
                                            arrow
                                            PopperProps={{
                                                modifiers: {
                                                    offset: {
                                                        enabled: true,
                                                        offset: "0, -8px",
                                                    },
                                                },
                                            }}
                                        >
                                            <Button
                                                fullWidth
                                                color="primary"
                                                size="small"
                                                onClick={handleLoadMore}
                                                style={{ textTransform: "none", fontSize: "0.8rem", fontWeight: 600 }}
                                            >
                                                Show more ({accumulatedInactive.length} / {inactiveData.total})
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                )}
                            </>
                        )}
                    </List>
                </Box>
            </Popover>
        </Box>
    );
}
